import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  removeFromCart, 
  updateQuantity, 
  clearCart,
  removeFromCartAsync,
  updateCartItemAsync,
  clearCartAsync,
  fetchCart
} from '../features/cart/cartSlice';
import Header from './Header';
import { BASE_URL, getImageUrl } from '../config';
import './CartPage.css';
import placeholderImage from '../images/Kurti1.avif';

const CartPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cartItems = useSelector((state) => state.cart.items);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  // Cart is for customers only; do not display to Admin or Seller
  useEffect(() => {
    if (userRole === 'Admin' || userRole === 'Seller') {
      navigate('/', { replace: true });
    }
  }, [userRole, navigate]);

  useEffect(() => {
    if (isLoggedIn && userRole !== 'Admin' && userRole !== 'Seller') {
      dispatch(fetchCart());
    }
  }, [dispatch, isLoggedIn, userRole]);

  if (userRole === 'Admin' || userRole === 'Seller') {
    return null;
  }

  const handleRemoveFromCart = (item) => {
    const payload = { productId: item.productId, variantId: item.variantId ?? null };
    if (isLoggedIn) {
      dispatch(removeFromCartAsync(payload));
    } else {
      dispatch(removeFromCart(payload));
    }
  };

  const handleUpdateQuantity = (item, quantity) => {
    if (quantity < 1) {
      handleRemoveFromCart(item);
      return;
    }
    const maxStock = Number(item.stock);
    if (Number.isFinite(maxStock) && quantity > maxStock) {
      const variantLabel = [item.colorName, item.sizeName].filter(Boolean).join(' ');
      window.alert(variantLabel ? `Only ${maxStock} in stock for ${variantLabel}.` : `Only ${maxStock} in stock.`);
      return;
    }
    const payload = { productId: item.productId, quantity, variantId: item.variantId ?? null };
    if (isLoggedIn) {
      dispatch(updateCartItemAsync(payload));
    } else {
      dispatch(updateQuantity(payload));
    }
  };

  const handleClearCart = () => {
    if (isLoggedIn) {
      dispatch(clearCartAsync());
    } else {
      dispatch(clearCart());
    }
  };

  const handleProceedToCheckout = () => {
    navigate('/checkout'); // Navigate to the checkout page
  };

  const totalAmount = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const deliveryCharges = cartItems.reduce((sum, item) => sum + (Number(item.deliveryCharge) || 0), 0);
  const grandTotal = totalAmount + deliveryCharges;

  return (
    <div className="cart-page-wrapper">
      <Header />
      <div className="cart-page-container">
        <h1>Your Shopping Cart</h1>
      {cartItems.length === 0 ? (
        <div className="empty-cart-message">
          <h2>Your cart is empty</h2>
          <p>Add some items to your cart to get started!</p>
        </div>
      ) : (
        <>
          <div className="cart-items-list">
            {cartItems.map((item) => {
              const originalPrice = item.originalPrice != null && Number(item.originalPrice) > Number(item.price) && Number(item.originalPrice) > 0
                ? Number(item.originalPrice)
                : null;
              const discountPercent = originalPrice
                ? Math.round(((originalPrice - item.price) / originalPrice) * 100)
                : 0;
              const subtotal = item.price * item.quantity;
              const subtotalMrp = originalPrice ? originalPrice * item.quantity : subtotal;
              const subtotalDiscount = subtotalMrp - subtotal;
              return (
                <div key={`${item.productId}-${item.variantId ?? 'none'}`} className="cart-item-card">
                  <img src={getImageUrl(item.imageUrl, placeholderImage)} alt={item.productName} className="cart-item-image" />
                  <div className="cart-item-details">
                    <h3>{item.productName}</h3>
                    {(item.sizeName || item.colorName) && (
                      <p className="cart-item-variant">
                        {[item.sizeName && `Size: ${item.sizeName}`, item.colorName && `Color: ${item.colorName}`].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    <div className="cart-item-price-block">
                      {originalPrice ? (
                        <>
                          <span className="cart-original-price">₹{originalPrice.toFixed(2)}</span>
                          <span className="cart-discount-badge">{discountPercent}% off</span>
                        </>
                      ) : null}
                      <p className="price">Price: ₹{item.price ? item.price.toFixed(2) : 'N/A'}</p>
                    </div>
                    <div className="quantity-control">
                      <label>Quantity:</label>
                      <button onClick={() => handleUpdateQuantity(item, item.quantity - 1)}>-</button>
                      <span>{item.quantity}</span>
                      <button
                        onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                        disabled={Number.isFinite(Number(item.stock)) && item.quantity >= Number(item.stock)}
                      >+</button>
                    </div>
                    <div className="cart-item-subtotal">
                      <p className="price">Subtotal: ₹{subtotal.toFixed(2)}</p>
                      {subtotalDiscount > 0 && (
                        <span className="cart-item-savings">You save ₹{subtotalDiscount.toFixed(2)}</span>
                      )}
                    </div>
                    <button onClick={() => handleRemoveFromCart(item)} className="remove-button">Remove</button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="cart-summary-section">
            <h3>Order Summary</h3>
            <div className="summary-row">
              <span>Total Items:</span>
              <span>{cartItems.reduce((total, item) => total + item.quantity, 0)}</span>
            </div>
            <div className="summary-row">
              <span>Items:</span>
              <span>₹{totalAmount.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Delivery:</span>
              <span>{deliveryCharges > 0 ? `₹${deliveryCharges.toFixed(2)}` : 'FREE'}</span>
            </div>
            <div className="summary-row total">
              <span>Total Amount:</span>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>
            <div className="button-group">
              <button onClick={handleClearCart} className="clear-cart-button">Clear Cart</button>
              <button onClick={handleProceedToCheckout} className="checkout-button">Proceed to Checkout</button>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
};

export default CartPage;
